import { useQuery } from '@tanstack/react-query';
import { apiClient, type ApiResponse } from '@/api';
import { TENANT_API_URLS } from '@/api/constants/api.url';
import { planCacheKeys } from '@/modules/auth/hooks/use-dev-login';
import { readStoredPlan, setStoredPlan } from '@/shared/utils/stored-plan';
import type { LoginPlan } from '@/modules/auth/types';
import { useIsPlatformAdmin } from '@/shared/hooks/use-is-platform-admin';

/**
 * Read the signed-in tenant's plan from the query cache, hydrated from
 * localStorage on first call so a page refresh doesn't blank it out.
 *
 * Login / workspace-switch mutations prime the cache (and `ps_plan`) imperatively,
 * so for those flows this query never runs. Signup, however, never touches that
 * cache — so when the cache AND localStorage are both empty (a fresh signup, or
 * cleared storage) we fetch the plan from the API and mirror it back. Without
 * this, the workspace badge fell back to "Free" until the user logged out and
 * back in. `initialData` is only seeded when a stored plan exists, so the empty
 * case actually triggers the fetch instead of resolving to a stale `null`.
 */
export function usePlan(): LoginPlan | null {
    const stored = readStoredPlan();
    const isPlatformAdmin = useIsPlatformAdmin();
    const { data } = useQuery<LoginPlan | null>({
        queryKey: planCacheKeys.current(),
        // The admin has no plan and the sidebar hides the badge for them
        // (`showPlan={!isAdmin}`), so this request is pure latency.
        enabled: !isPlatformAdmin,
        queryFn:  async () => {
            try {
                const res = await apiClient.get<ApiResponse<{ plan: LoginPlan | null }>>(
                    TENANT_API_URLS.plan,
                );
                setStoredPlan(res.data.data.plan);
                return res.data.data.plan;
            } catch {
                // API hiccup / not signed in — fall back to whatever's stored.
                return readStoredPlan();
            }
        },
        initialData: stored ?? undefined,
        // Bounded rather than Infinity: `usePlan` is mounted for the whole
        // session by the sidebar workspace card, so an Infinity staleTime meant
        // the query never refetched — a Super-Admin change to a plan's name,
        // limits or features (e.g. a QR grant) could not reach a signed-in
        // tenant until they logged out and back in. `initialDataUpdatedAt: 0`
        // marks the localStorage seed as already stale, so the first mount
        // after a refresh revalidates in the background while rendering the
        // stored plan instantly — no flash, no gate flicker.
        initialDataUpdatedAt: 0,
        staleTime:   5 * 60_000,
        gcTime:      Infinity,
    });
    return data ?? null;
}
